import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Eye, EyeOff, Lock, User as UserIcon, ShieldCheck } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
    const { login, school } = useAuth();
    const nav = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotUser, setForgotUser] = useState("");

    const detectedRole = (() => {
        const u = username.trim().toLowerCase();
        if (!u) return null;
        if (u.startsWith("s") && /^s\d+$/.test(u)) return "طالبة";
        if (u.startsWith("u") && /^u\d+$/.test(u)) return "ولي أمر";
        if (u === "admin") return "إدارة";
        return "مدربة / حساب مخصص";
    })();

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const u = await login(username.trim(), password);
            toast.success("تم تسجيل الدخول بنجاح");
            if (u.must_change_password) return nav("/change-password", { replace: true });
            if (u.role === "admin") return nav("/admin", { replace: true });
            if (u.role === "trainer") return nav("/trainer", { replace: true });
            if (u.role === "student") return nav("/student", { replace: true });
            if (u.role === "parent") return nav("/parent", { replace: true });
            nav("/", { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2, "تعذر تسجيل الدخول"));
        } finally {
            setBusy(false);
        }
    };

    const submitForgot = async () => {
        try {
            await api.post("/auth/forgot-password", { username: forgotUser.trim() });
            toast.success("تم إرسال طلبك للإدارة");
            setForgotOpen(false);
            setForgotUser("");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div dir="rtl" className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-white">
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: "url(/login-bg.jpg)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                }}
            />
            <div aria-hidden="true" className="absolute inset-0 bg-white/40 pointer-events-none" />
            <div className="relative z-10 w-full max-w-5xl grid md:grid-cols-5 gap-8 items-center">
                {/* Brand panel */}
                <div className="md:col-span-2 text-[#0F172A] space-y-6 hidden md:block login-brand-enter">
                    <BrandLogo size={72} textClass="text-[#0F172A]" />
                    <div className="space-y-3">
                        <h1 className="text-4xl font-bold leading-tight text-[#0F172A]">
                            منصة <span className="text-[#C8102E]">بقدراتنا نسمو</span>
                        </h1>
                        <p className="text-slate-700 leading-relaxed text-sm max-w-md">
                            منصة تعليمية تهدف إلى تنمية مهارات طالباتنا في القدرات اللفظية والكمية، وتهيئتهن لاختبار القدرات من خلال محتوى تدريبي منظم ومتابعة مستمرة؛ إيمانًا بأن الاستعداد المبكر والتدريب المستمر هما طريق التميز والنجاح.
                        </p>
                        <div className="pt-2 space-y-1 text-sm text-[#0F172A]">
                            <p><span className="text-slate-500">إعداد وإشراف المدربة:</span> <span className="font-semibold">ياسمين حرارة</span></p>
                            <p><span className="text-slate-500">مديرة المدرسة:</span> <span className="font-semibold">هدى السفياني</span></p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 bg-[#0F172A]/5 rounded-xl border border-[#0F172A]/10 p-4 max-w-md backdrop-blur-sm">
                        <ShieldCheck className="w-5 h-5 text-[#1B2A4E] shrink-0 mt-0.5" />
                        <p className="text-xs text-slate-700 leading-relaxed">
                            الدخول مخصص للحسابات المُنشأة من قِبل الإدارة فقط. لا يوجد تسجيل ذاتي — تواصلي مع إدارة المدرسة لأي استفسار.
                        </p>
                    </div>
                </div>

                {/* Login card */}
                <div className="md:col-span-3 login-card-enter">
                    <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8 lg:p-10 border border-white/40">
                        <div className="md:hidden mb-6">
                            <BrandLogo size={54} textClass="text-[#1B2A4E]" />
                        </div>
                        <div className="mb-7">
                            <h2 className="text-2xl font-bold text-[#0F172A]">تسجيل الدخول</h2>
                            <p className="text-sm text-slate-500 mt-1">أدخلي بيانات حسابك للوصول للنظام</p>
                        </div>

                        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                            <div>
                                <Label className="text-slate-700 mb-1.5 block">اسم المستخدم</Label>
                                <div className="relative">
                                    <UserIcon className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        data-testid="login-username-input"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="مثال: s1234567890 أو admin"
                                        className="pr-10 h-11 text-base font-mono-num placeholder:font-normal placeholder:text-slate-400"
                                        dir="ltr"
                                        autoComplete="username"
                                    />
                                </div>
                                {detectedRole && (
                                    <div className="mt-1.5 text-xs text-slate-500">
                                        نوع الحساب المتوقع: <span className="text-[#1B2A4E] font-semibold">{detectedRole}</span>
                                    </div>
                                )}
                            </div>

                            <div>
                                <Label className="text-slate-700 mb-1.5 block">كلمة المرور</Label>
                                <div className="relative">
                                    <Lock className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        data-testid="login-password-input"
                                        type={showPw ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="pr-10 pl-10 h-11 text-base"
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((s) => !s)}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        data-testid="toggle-password"
                                    >
                                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {err && (
                                <Alert variant="destructive" data-testid="login-error-alert" className="bg-[#FFF1F2] border-[#FECACA] text-[#991B1B]">
                                    <AlertDescription>{err}</AlertDescription>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                disabled={busy}
                                data-testid="login-submit-button"
                                className="w-full h-11 bg-[#1B2A4E] hover:bg-[#0F172A] text-white font-semibold text-base"
                            >
                                {busy ? "جارٍ الدخول..." : "تسجيل الدخول"}
                            </Button>

                            <div className="flex items-center justify-between pt-1">
                                <button
                                    type="button"
                                    onClick={() => setForgotOpen(true)}
                                    className="text-sm text-[#C8102E] hover:underline"
                                    data-testid="forgot-password-link"
                                >
                                    نسيت كلمة المرور؟
                                </button>
                                <div className="text-[11px] text-slate-400">
                                    الحسابات تُنشأ بواسطة الإدارة فقط
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="mt-4 text-center text-xs text-slate-500">
                        {school.name} • {new Date().getFullYear()}
                    </div>
                </div>
            </div>

            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
                <DialogContent dir="rtl" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>نسيت كلمة المرور</DialogTitle>
                        <DialogDescription>
                            أدخلي اسم المستخدم وسيتم إرسال طلب إعادة التعيين للإدارة.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        value={forgotUser}
                        onChange={(e) => setForgotUser(e.target.value)}
                        placeholder="اسم المستخدم"
                        dir="ltr"
                        className="font-mono-num"
                        data-testid="forgot-username-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setForgotOpen(false)}>إلغاء</Button>
                        <Button onClick={submitForgot} data-testid="forgot-submit-btn" className="bg-[#1B2A4E] hover:bg-[#0F172A]">إرسال</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
